import React, { useState, useCallback, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  TextInput,
  ScrollView,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../utils/responsive';
import { useNavigation, useFocusEffect, useRoute } from '@react-navigation/native';
import { RootStackNavigationProp } from '../../../types/navigation';
import { BottomNavBar } from '../../../components/BottomNavBar';
import { Expense } from '../../../services/expenseService';
import { expenseService } from '../../../services/expenseService';



import { createLogger } from '../../../utils/logger';
const logger = createLogger('ExpensesDashboard');

type CategoryData = {
  id: string;
  name: string;
  icon: any;
  count: number;
  color: string;
  expenses: Expense[];
};

// Categories data with icons and counts
const categories: CategoryData[] = [
  { id: 'all', name: 'All', icon: require('../../../assets/icons/allExpenses.png'), count: 0, color: '#FFFFFF', expenses: [] },
  { id: 'housing', name: 'Housing', icon: require('../../../assets/icons/house.png'), count: 0, color: '#FFFFFF', expenses: [] },
  { id: 'taxes', name: 'Taxes', icon: require('../../../assets/icons/financial.png'), count: 0, color: '#FFFFFF', expenses: [] },
  { id: 'utilities', name: 'Utilities', icon: require('../../../assets/icons/utilities.png'), count: 0, color: '#FFFFFF', expenses: [] },
  { id: 'insurance', name: 'Insurance', icon: require('../../../assets/icons/insurance.png'), count: 0, color: '#FFFFFF', expenses: [] },
  { id: 'credit-cards', name: 'Credit Card', icon: require('../../../assets/icons/credit-card.png'), count: 0, color: '#FFFFFF', expenses: [] },
  { id: 'loans', name: 'Loans', icon: require('../../../assets/icons/bank.png'), count: 0, color: '#FFFFFF', expenses: [] },
  { id: 'healthcare', name: 'Healthcare', icon: require('../../../assets/icons/medical.png'), count: 0, color: '#FFFFFF', expenses: [] },
  { id: 'memberships', name: 'Memberships', icon: require('../../../assets/icons/memberships.png'), count: 0, color: '#FFFFFF', expenses: [] },
  { id: 'subscriptions', name: 'Subscriptions', icon: require('../../../assets/icons/subscriptions.png'), count: 0, color: '#FFFFFF', expenses: [] },
  { id: 'investments', name: 'Investments', icon: require('../../../assets/icons/investments.png'), count: 0, color: '#FFFFFF', expenses: [] },
  { id: 'personal', name: 'Personal', icon: require('../../../assets/icons/user.png'), count: 0, color: '#FFFFFF', expenses: [] },
  { id: 'other', name: 'Other', icon: require('../../../assets/icons/misc.png'), count: 0, color: '#FFFFFF', expenses: [] },
];

type CategoryCardProps = {
  item: CategoryData;
  isSelected: boolean;
  onPress: () => void;
  navigation: RootStackNavigationProp;
};

const CategoryCard: React.FC<CategoryCardProps> = ({ item, isSelected, onPress, navigation }) => {
  // Card click now filters the dashboard instead of navigating
  const handleCardPress = () => {
    onPress(); // This calls handleCategorySelect to filter the current dashboard
  };

  return (
    <TouchableOpacity
      style={[
        styles.categoryCard,
        { backgroundColor: isSelected ? '#29BFC0' : '#FFFFFF' }
      ]}
      onPress={handleCardPress}
    >
      <View style={styles.categoryIconContainer}>
        <Image source={item.icon} style={styles.categoryIcon} />
      </View>
      <Text style={[
        styles.categoryName,
        { color: isSelected ? '#FFFFFF' : '#1C2C4F' }
      ]}>{item.name}</Text>
      <Text style={[
        styles.categoryCount,
        { color: isSelected ? '#FFFFFF' : '#6B7280' }
      ]}>{item.count} {item.count === 1 ? 'Expense' : 'Expenses'}</Text>
      <TouchableOpacity
        style={[
          styles.addButton,
          { backgroundColor: isSelected ? '#FFFFFF' : '#29BFC0' }
        ]}
        onPress={() => navigation.navigate('ExpensesInputScreen' as any, { category: item.id })}
      >
        <Image
          source={require('../../../assets/icons/add.png')}
          style={[
            styles.addIcon,
            { tintColor: isSelected ? '#29BFC0' : '#FFFFFF' }
          ]}
        />
      </TouchableOpacity>
    </TouchableOpacity>
  );
};

type ExpenseItemProps = {
  item: Expense;
  onPress: (expense: Expense) => void;
};

const ExpenseItem: React.FC<ExpenseItemProps> = ({ item, onPress }) => {
  const getIcon = (category: string) => {
    const categoryData = categories.find(cat => cat.id === category.toLowerCase());
    return categoryData ? categoryData.icon : categories[0].icon;
  };

  return (
    <TouchableOpacity
      style={styles.expenseItem}
      onPress={() => onPress(item)}
    >
      <View style={styles.expenseIcon}>
        <Image source={getIcon(item.category)} style={styles.expenseIconImage} />
      </View>
      <View style={styles.expenseInfo}>
        <Text style={styles.expenseName}>{item.title}</Text>
        <Text style={styles.expenseAmount}>
          ${item.amount ? (typeof item.amount === 'number' ? item.amount.toFixed(2) : parseFloat(item.amount).toFixed(2)) : '0.00'}
        </Text>
      </View>
      <View style={styles.expenseType}>
        {item.provider && <Text style={styles.expenseProviderTop}>{item.provider}</Text>}
        <TouchableOpacity style={styles.moreButton}>
          <Text style={styles.moreButtonText}>•••</Text>
        </TouchableOpacity>
      </View>
    </TouchableOpacity>
  );
};

export const ExpensesDashboardScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute();
  const [, setAllExpenses] = useState<Expense[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [categoryData, setCategoryData] = useState<CategoryData[]>(categories);
  const [searchText, setSearchText] = useState('');

  // Handle selectedCategory parameter from navigation
  useEffect(() => {
    const params = route.params as any;
    if (params?.selectedCategory) {
      setSelectedCategory(params.selectedCategory);
    }
  }, [route.params]);

  useFocusEffect(
    useCallback(() => {
      loadExpenses();
    }, [])
  );

  const loadExpenses = async () => {
    setLoading(true);
    try {
      // Fetch expenses from expenseService (API)
      const expensesData = await expenseService.getAllExpenses();
      logger.debug('Loaded expenses from service:', expensesData.length);
      logger.debug('First expense data:', expensesData[0]); // Debug log
      setAllExpenses(expensesData);
      
      // Update category data with expense counts
      const updatedCategories = categories.map(category => {
        if (category.id === 'all') {
          return {
            ...category,
            count: expensesData.length,
            expenses: expensesData,
          };
        } else {
          const categoryExpenses = expensesData.filter(e => e.category.toLowerCase() === category.id.toLowerCase());
          return {
            ...category,
            count: categoryExpenses.length,
            expenses: categoryExpenses,
          };
        }
      });
      
      setCategoryData(updatedCategories);
    } catch (error) {
      logger.error('Error loading expenses:', error);
      Alert.alert('Error', 'Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategory(categoryId);
  };

  const handleExpensePress = (expense: Expense) => {
    navigation.navigate('ExpensesDataScreen' as any, { expense });
  };

  const handleAddExpense = () => {
    navigation.navigate('ExpensesInputScreen' as any);
  };

  const selectedCategoryData = categoryData.find(cat => cat.id === selectedCategory);
  const displayExpenses = selectedCategoryData?.expenses || [];

  const filteredExpenses = displayExpenses.filter(expense =>
    searchText === '' ||
    expense.title.toLowerCase().includes(searchText.toLowerCase()) ||
    expense.provider?.toLowerCase().includes(searchText.toLowerCase()) ||
    expense.category.toLowerCase().includes(searchText.toLowerCase())
  );

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>Loading expenses...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../assets/icons/back.png')}
              style={[styles.backIcon, { tintColor: '#333' }]}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <View style={styles.searchContainer}>
          <TextInput
            style={styles.searchInput}
            placeholder="Search expenses"
            placeholderTextColor="#9CA3AF"
            value={searchText}
            onChangeText={setSearchText}
          />
          <Image
            source={require('../../../assets/icons/search.png')}
            style={styles.searchIcon}
          />
        </View>

        <View style={styles.titleContainer}>
          <Text style={styles.manageText}>Manage</Text>
          <Text style={styles.allExpensesText}>All Expenses</Text>
        </View>

        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.categoriesList}
          contentContainerStyle={{ paddingRight: 30 }}
        >
          {categoryData.map((item) => (
            <CategoryCard
              key={item.id}
              item={item}
              isSelected={selectedCategory === item.id}
              onPress={() => handleCategorySelect(item.id)}
              navigation={navigation}
            />
          ))}
        </ScrollView>

        <View style={styles.expensesContainer}>
          {filteredExpenses.length === 0 ? (
            <View style={styles.emptyState}>
              <Text style={styles.emptyTitle}>
                {selectedCategory === 'all' ? 'No Expenses Yet' : `No ${selectedCategoryData?.name} Expenses`}
              </Text>
              <Text style={styles.emptySubtitle}>
                {selectedCategory === 'all' 
                  ? 'Start by adding your first expense to keep them organized'
                  : `Add your first ${selectedCategoryData?.name.toLowerCase()} expense`
                }
              </Text>
              <TouchableOpacity style={styles.mainAddButton} onPress={handleAddExpense}>
                <Text style={styles.addButtonText}>
                  {selectedCategory === 'all' ? 'Add Your First Expense' : `Add ${selectedCategoryData?.name} Expense`}
                </Text>
              </TouchableOpacity>
            </View>
          ) : (
            filteredExpenses.map((item) => (
              <ExpenseItem key={item.id} item={item} onPress={handleExpensePress} />
            ))
          )}
        </View>
      </ScrollView>
      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  scrollView: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '25%',
  },
  notificationIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  searchContainer: {
    margin: ms(20),
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    paddingHorizontal: ms(15),
  },
  searchInput: {
    flex: 1,
    height: s(50),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  searchIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#6B7280',
  },
  titleContainer: {
    paddingHorizontal: ms(20),
    marginBottom: ms(15),
  },
  manageText: {
    fontSize: ms(16, 0.3),
    color: '#29BFC0',
    marginBottom: ms(5),
  },
  allExpensesText: {
    fontSize: ms(24, 0.3),
    color: '#FF9F5A',
    fontWeight: '600',
  },
  categoriesList: {
    paddingLeft: ms(20),
    marginBottom: ms(25),
    paddingBottom: ms(10),
  },
  expensesContainer: {
    paddingHorizontal: ms(20),
    paddingBottom: ms(120),
  },
  categoryCard: {
    width: s(130),
    height: s(160),
    borderRadius: ms(15),
    padding: ms(15),
    marginRight: ms(15),
    alignItems: 'center',
    justifyContent: 'space-between',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  categoryIconContainer: {
    width: s(40),
    height: s(40),
    borderRadius: ms(20),
    backgroundColor: '#F3F6F8',
    alignItems: 'center',
    justifyContent: 'center',
  },
  categoryIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#29BFC0',
  },
  categoryName: {
    fontSize: ms(14, 0.3),
    fontWeight: '500',
    textAlign: 'center',
  },
  categoryCount: {
    fontSize: ms(12, 0.3),
    textAlign: 'center',
  },
  addButton: {
    width: s(30),
    height: s(30),
    borderRadius: ms(15),
    alignItems: 'center',
    justifyContent: 'center',
  },
  addIcon: {
    width: s(15),
    height: s(15),
  },
  expenseItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: ms(15),
    padding: ms(15),
    marginBottom: ms(15),
    // Android
    elevation: 2,
    // iOS
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  expenseIcon: {
    width: s(40),
    height: s(40),
    borderRadius: ms(20),
    backgroundColor: '#29BFC0',
    tintColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
  },
  expenseIconImage: {
    width: s(25),
    height: s(25),
  },
  expenseInfo: {
    flex: 1,
    marginLeft: ms(15),
  },
  expenseName: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    fontWeight: '500',
    marginBottom: ms(5),
  },
  expenseAmount: {
    fontSize: ms(14, 0.3),
    color: '#6B7280',
  },
  expenseType: {
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    minHeight: s(50),
  },
  expenseProviderTop: {
    fontSize: ms(14, 0.3),
    color: '#6B7280',
    marginBottom: ms(2),
    textAlign: 'right',
  },
  moreButton: {
    padding: ms(5),
  },
  moreButtonText: {
    fontSize: ms(20, 0.3),
    color: '#6B7280',
    letterSpacing: ms(2),
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: ms(50),
  },
  emptyTitle: {
    fontSize: ms(20, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(8),
    textAlign: 'center',
  },
  emptySubtitle: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
    marginBottom: ms(30),
    textAlign: 'center',
    paddingHorizontal: ms(20),
  },
  mainAddButton: {
    backgroundColor: '#29BFC0',
    paddingHorizontal: ms(30),
    paddingVertical: ms(15),
    borderRadius: ms(25),
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  addButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  emptyStateText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
  },
});
